import React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import closeIcon from "../../assets/images/icon_close.svg";
import { AppDispatch } from "app/store";
import { useDispatch } from "react-redux";
import { addDownloadRecordAsync } from "features/download";

interface DownloadPopupProps {
  productId: string;
  name: string;
  version: string;
  libVer: string;
  releaseDate: string;
  filesize: string;
  downloadUrl: string;
  onCancel: () => void;
}

const DownloadPopup: React.FC<DownloadPopupProps> = (
  props: DownloadPopupProps
): JSX.Element => {
  const dispatch: AppDispatch = useDispatch();
  const {
    productId,
    name,
    version,
    libVer,
    releaseDate,
    filesize,
    downloadUrl,
    onCancel,
  } = props;
  const { t } = useTranslation();
  const libVerVisible = useMemo(() => !!libVer, [libVer]);
  const retry = async (fn: () => Promise<any>, retries = 3, delay = 1000) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        console.warn(`Retry ${attempt}/${retries} error:`, error);
        if (attempt === retries) throw error;
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  };
  const handleExecuteDownload = async ({
    downloadUrl,
    name,
    productId,
    dispatch,
  }: {
    downloadUrl: string;
    name: string;
    productId: string;
    dispatch: any;
  }) => {
    try {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${name}_license`;
      link.click();
      link.remove();
      await retry(
        () =>
          dispatch(
            addDownloadRecordAsync({
              productId: productId,
              productName: name,
            })
          ).unwrap(),
        3,
        1500
      );
      console.log("Download and save history success");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal>
      <ModalBox>
        <Title>
          {t("downloadPage.label.popupTitle")}
          <button
            type="button"
            onClick={() => {
              onCancel();
            }}
          >
            <img src={closeIcon} alt="" />
          </button>
        </Title>
        <form>
          <List>
            <ModalDt>{t("downloadPage.table.productName")}</ModalDt>
            <ModalDd>{name}</ModalDd>
            <ModalDt>{t("downloadPage.table.version")}</ModalDt>
            <ModalDd>{version}</ModalDd>
            {libVerVisible ? (
              <>
                <ModalDt>{t("downloadPage.table.libVer")}</ModalDt>
                <ModalDd>{!!libVer ? libVer : "-"}</ModalDd>
              </>
            ) : (
              <></>
            )}
            <ModalDt>{t("downloadPage.table.releaseDate")}</ModalDt>
            <ModalDd>{releaseDate}</ModalDd>
            <ModalDt>{t("downloadPage.table.fileSize")}</ModalDt>
            <ModalDd>{filesize}</ModalDd>
            <ButtonArea>
              <CancelButton
                onClick={() => {
                  onCancel();
                }}
              >
                {t("common.button.cancel")}
              </CancelButton>
              <ExecuteButton
                onClick={() =>
                  handleExecuteDownload({
                    downloadUrl,
                    name,
                    productId,
                    dispatch,
                  })
                }
              >
                {t("common.button.execute")}
              </ExecuteButton>
            </ButtonArea>
          </List>
        </form>
      </ModalBox>
    </Modal>
  );
};

export default DownloadPopup;

const Modal = styled.div`
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  position: fixed;
  z-index: 5;
`;
const ModalBox = styled.div`
  display: block;
  width: 800px;
  padding: 2rem;
  background: #fff;
  box-shadow: 0 0 10px #333;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const Title = styled.p`
  display: flex;
  justify-content: space-between;
  margin-bottom: 3rem;
  font-size: 1.71rem;
  line-height: 2rem;
  letter-spacing: 0.07rem;
  font-weight: normal;
`;
const List = styled.dl`
  display: flex;
  flex-wrap: wrap;
`;
const ButtonArea = styled.dd`
  width: 100%;
  padding-top: 2rem;
  text-align: center;
`;
const ExecuteButton = styled.a`
  width: 10rem;
  padding: 1rem 3rem;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
  cursor: pointer;
  opacity: 1;
  color: #ffffff;
  background: #005eb8;
  border: 1px #005eb8 solid;
  pointer-events: auto;
  :hover {
    background: rgba(0, 94, 184, 0.7);
  }
  -moz-transition: all 0.3s ease-out;
  -ms-transition: all 0.3s ease-out;
  -webkit-transition: all 0.3s ease-out;
  transition: all 0.3s ease-out;
  text-decoration: none;
`;

const CancelButton = styled.p`
  color: #005eb8;
  text-decoration: underline;
  cursor: pointer;
  display: inline-block;
  margin: 0 3rem;
  font-size: 14px;
  line-height: 1.4;
  letter-spacing: 0;
  font-weight: normal;
`;
const ModalDt = styled.dt`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  width: 20%;
  padding: 1rem 2%;
`;
const ModalDd = styled.dd`
  font-size: 14px;
  line-height: 1.7;
  letter-spacing: 0;
  font-weight: normal;
  width: 76%;
  padding: 1rem 0;
`;
